import React, { useCallback, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { Box } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { ICreateDocumentAggregatorDtoDocumentTypeEnum } from '@/dtos';
import LettersOfMedicalNecessityPopup from '@/modules/MyDocuments/components/LettersOfMedicalNecessity/LettersOfMedicalNecessityPopup/LettersOfMedicalNecessityPopup';
import { LMN_UPLOAD_STORE_KEY, LMNPopupMode } from '@/modules/MyDocuments/myDocuments.types';
import useDeleteLettersOfMedicalNecessityQuery from '@/modules/MyDocuments/queries/useDeleteLettersOfMedicalNecessity.query';
import useRemoveDocumentQuery from '@/modules/transaction/queries/useRemoveDocument.query';
import useRemoveFileQuery from '@/modules/transaction/queries/useRemoveFile.query';
import { UnsavedChangesPrompt } from '@/modules/UseMyMoney/components';
import { useMyMoneyNavigation, useResetUseMyMoney } from '@/modules/UseMyMoney/hooks';
import {
  useCreateClaimQuery,
  useGetDocumentOcrOptionsQuery,
  useGetOcrOptionsQuery,
} from '@/modules/UseMyMoney/queries';
import { useDocumentOcrStore, useMyMoneyFormStore, useMyMoneyStore } from '@/modules/UseMyMoney/stores';
import { USE_MY_MONEY_UPLOAD_NEW_FILE_STORE_KEY, USE_MY_MONEY_UPLOAD_STORE_KEY } from '@/modules/UseMyMoney/UseMyMoney.types';
import FieldSetupContainer from '@/modules/UseMyMoney/widgets/FieldSetup/FieldSetup.container';
import SavedReceiptsPopupContainer from '@/modules/UseMyMoney/widgets/SavedReceipts/SavedReceiptsPopup.container';
import UploadFilePopupContainer from '@/modules/UseMyMoney/widgets/UploadFile/UploadFilePopup.container';
import UploadNewFileContainer from '@/modules/UseMyMoney/widgets/UploadNewFile/UploadNewFile.container';
import { ClaimSuccessfullyCreatedPopup } from '@/modules/UseMyMoney/widgets/UseMyMoneySummary/components';
import {
  useClearInvalidServiceDate,
  useHasAvailableAccounts,
  useSetupClaimSummaryFieldsFromOCR,
} from '@/modules/UseMyMoney/widgets/UseMyMoneySummary/hooks';

import { ClaimSummary } from './components';

const UseMyMoneySummaryContainer = () => {
  const {
    values,
    isClaimAutomaticallyApproved,
    isClaimCreatedWithoutDocument,
    handleSetFormValues,
  } = useMyMoneyFormStore();
  const { useMyMoneyOption } = useMyMoneyStore();
  const { ocrDocument, isOcrCompleted } = useDocumentOcrStore();
  const { activeField, handleSetActiveField } = useMyMoneyNavigation();

  useResetUseMyMoney();

  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [isSelectLmnPopupVisible, setIsSelectLmnPopupVisible] = useState(false);
  const [isAddLmnPopupVisible, setIsAddLmnPopupVisible] = useState(false);
  const [isSavedReceiptsPopupVisible, setIsSavedReceiptsPopupVisible] = useState(false);
  const [isUploadNewFilePopupVisible, setIsUploadNewFilePopupVisible] = useState(false);

  const onLmnUpload = useCallback(() => {
    setIsSelectLmnPopupVisible(false);
    setIsAddLmnPopupVisible(true);
  }, []);

  const handleOpenSavedReceiptsPopup = useCallback(() => {
    setIsSavedReceiptsPopupVisible(true);
    setIsSelectLmnPopupVisible(false);
  }, []);

  const handleSetLmnId = useCallback((lmnId?: string) => {
    handleSetFormValues({ lmnId, isLmnWasUploaded: true });
  }, [handleSetFormValues]);

  const handleOpenFileUploadPopup = useCallback(() => {
    setIsUploadNewFilePopupVisible(true);
  }, []);

  const { createClaim, isCreateClaimLoading } = useCreateClaimQuery();
  const { handleDelete } = useDeleteLettersOfMedicalNecessityQuery();
  const { removeFile } = useRemoveFileQuery();
  const { remove: removeDocument } = useRemoveDocumentQuery();

  const { values: documentValues } = useFilesInDocumentStore(USE_MY_MONEY_UPLOAD_STORE_KEY);

  const { documentOcrOptions, isDocumentOcrOptionsLoading } = useGetDocumentOcrOptionsQuery({
    documentId: isOcrCompleted ? documentValues?.documentId : undefined,
    employeeId: values?.patientId,
    dependentId: values?.patientId,
    serviceOn: values?.dateOfService,
    amount: values?.amount,
    serviceId: values?.serviceId,
    providerId: values?.providerId,
  });

  const { ocrOptions, isOcrOptionsLoading } = useGetOcrOptionsQuery({
    documentId: isOcrCompleted ? documentValues?.documentId : undefined,
    employeeId: values?.patientId,
    dependentId: values?.patientId,
    serviceOn: values?.dateOfService,
    amount: values?.amount,
    serviceId: values?.serviceId,
    providerId: values?.providerId,
  });

  useSetupClaimSummaryFieldsFromOCR({ documentOcrOptions: documentValues?.documentId ? documentOcrOptions : ocrOptions });

  const { hasAvailableAccounts } = useHasAvailableAccounts({
    accounts: ocrDocument?.accountOrders,
    dateOfService: values?.dateOfService,
    isAccountOrdersLoading: isDocumentOcrOptionsLoading || isOcrOptionsLoading,
  });

  useClearInvalidServiceDate({ hasAvailableAccounts });

  return (
    <Box data-testid="use-my-money-summary_container" flex="grow">
      <UnsavedChangesPrompt />
      <Switch>
        <Route key={ROUTES.USE_MY_MONEY_SUMMARY} path={ROUTES.USE_MY_MONEY_SUMMARY} exact>
          <ClaimSummary
            isAccountsLoading={isDocumentOcrOptionsLoading || isOcrOptionsLoading}
            setIsSelectLmnPopupVisible={setIsSelectLmnPopupVisible}
            onDeleteLmn={handleDelete}
            setActiveField={handleSetActiveField}
            createClaim={createClaim}
            onUploadFile={handleOpenFileUploadPopup}
            onDeleteFile={removeFile}
            onDeleteDocument={removeDocument}
            setIsSuccessPopupVisible={setIsSuccessPopupVisible}
            isCreateClaimLoading={isCreateClaimLoading}
            hasAvailableAccounts={hasAvailableAccounts}
            isUploadNewFilePopupVisible={isUploadNewFilePopupVisible}
          />
        </Route>
        <Route key={ROUTES.USE_MY_MONEY_SETUP_FIELDS} path={ROUTES.USE_MY_MONEY_SETUP_FIELDS} exact>
          <FieldSetupContainer
            activeField={activeField}
            setActiveField={handleSetActiveField}
            useMyMoneyOption={useMyMoneyOption}
            hasAvailableAccounts={hasAvailableAccounts}
            onUploadFile={handleOpenFileUploadPopup}
            onDeleteFile={removeFile}
            onDeleteDocument={removeDocument}
            isUploadNewFilePopupVisible={isUploadNewFilePopupVisible}
          />
        </Route>
      </Switch>

      {isSelectLmnPopupVisible && (
        <UploadFilePopupContainer
          storeKey={LMN_UPLOAD_STORE_KEY}
          onUpload={onLmnUpload}
          onQrCodeUpload={onLmnUpload}
          onSetVisible={setIsSelectLmnPopupVisible}
          onSelectFromSavedReceipts={handleOpenSavedReceiptsPopup}
          documentType={ICreateDocumentAggregatorDtoDocumentTypeEnum.LETTER_OF_MEDICAL_NECESSITY}
          disableFilesUpload
          disableRemoveDocumentOnError
        />
      )}

      {isUploadNewFilePopupVisible && (
        <UploadNewFileContainer
          onSetVisible={setIsUploadNewFilePopupVisible}
          storeKey={USE_MY_MONEY_UPLOAD_NEW_FILE_STORE_KEY}
          onSuccessFileUpload={() => setIsSuccessPopupVisible(true)}
          setIsSuccessPopupVisible={setIsSuccessPopupVisible}
        />
      )}

      {isAddLmnPopupVisible && (
        <LettersOfMedicalNecessityPopup
          mode={LMNPopupMode.UPLOAD}
          onSetVisible={setIsAddLmnPopupVisible}
          onSuccess={handleSetLmnId}
          disabledClaimSelect
          disableResetDocumentStore
        />
      )}

      {isSavedReceiptsPopupVisible && (
        <SavedReceiptsPopupContainer onSetVisible={setIsSavedReceiptsPopupVisible} />
      )}

      {isSuccessPopupVisible && (
        <ClaimSuccessfullyCreatedPopup
          createdClaimId={values?.createdClaimId}
          onSetVisible={setIsSuccessPopupVisible}
          onUploadFile={handleOpenFileUploadPopup}
          isClaimAutomaticallyApproved={isClaimAutomaticallyApproved}
          isClaimCreatedWithoutDocument={isClaimCreatedWithoutDocument}
        />
      )}
    </Box>
  );
};

export default UseMyMoneySummaryContainer;
